import { createReducer, on } from '@ngrx/store';

import { SupplierState } from '../survey-state.model';
import { INITIAL_SUPPLIER_STATE } from '../survey-state.constants';
import { ManageSurveyActions } from '../manage-survey/manage-survey.actions';
import { SupplierActions, SupplierApiActions } from './supplier.actions';

export const supplierReducer = createReducer(
  INITIAL_SUPPLIER_STATE,

  on(
    SupplierActions.loadAllocations,
    (state): SupplierState => ({
      ...state,
      allocationsLoading: true,
      allocationsError: null,
    }),
  ),

  // Switching to a child survey shows a different survey's allocations, so the
  // previous list is dropped rather than left on screen under the spinner.
  on(
    ManageSurveyActions.loadChildSurveyDetails,
    (): SupplierState => ({
      allocations: [],
      allocationsLoading: true,
      allocationsError: null,
      allocationsUpdateStatus: 'idle',
      blends: [],
      blendsLoading: false,
      blendsError: null,
      flexibilityPriceParams: null,
      flexibilityPriceParamsLoading: false,
      flexibilityPriceParamsError: null,
    }),
  ),

  on(
    SupplierActions.loadBlends,
    (state): SupplierState => ({
      ...state,
      blendsLoading: true,
      blendsError: null,
    }),
  ),

  on(
    SupplierApiActions.loadBlendsSuccess,
    (state, { blends }): SupplierState => ({
      ...state,
      blends,
      blendsLoading: false,
      blendsError: null,
    }),
  ),

  on(
    SupplierApiActions.loadBlendsFailure,
    (state, { error }): SupplierState => ({
      ...state,
      blends: [],
      blendsLoading: false,
      blendsError: error,
    }),
  ),

  on(
    SupplierActions.loadFlexibilityPriceParams,
    (state): SupplierState => ({
      ...state,
      flexibilityPriceParamsLoading: true,
      flexibilityPriceParamsError: null,
    }),
  ),

  on(
    SupplierApiActions.loadFlexibilityPriceParamsSuccess,
    (state, { params }): SupplierState => ({
      ...state,
      flexibilityPriceParams: params,
      flexibilityPriceParamsLoading: false,
      flexibilityPriceParamsError: null,
    }),
  ),

  on(
    SupplierApiActions.loadFlexibilityPriceParamsFailure,
    (state, { error }): SupplierState => ({
      ...state,
      flexibilityPriceParams: null,
      flexibilityPriceParamsLoading: false,
      flexibilityPriceParamsError: error,
    }),
  ),

  on(
    SupplierApiActions.loadAllocationsSuccess,
    (state, { allocations }): SupplierState => ({
      ...state,
      allocations,
      allocationsLoading: false,
      allocationsError: null,
    }),
  ),

  on(
    SupplierApiActions.loadAllocationsFailure,
    (state, { error }): SupplierState => ({
      ...state,
      allocations: [],
      allocationsLoading: false,
      allocationsError: error,
    }),
  ),

  on(
    SupplierActions.updateAllocations,
    (state): SupplierState => ({
      ...state,
      allocationsUpdateStatus: 'saving',
    }),
  ),

  // The PUT returns the survey's updated allocations, so its response replaces
  // the list outright — no follow-up GET is needed.
  on(
    SupplierApiActions.updateAllocationsSuccess,
    (state, { allocations }): SupplierState => ({
      ...state,
      allocations,
      allocationsLoading: false,
      allocationsError: null,
      allocationsUpdateStatus: 'saved',
    }),
  ),

  // The previous list survives a failed update so the drawer can stay open on
  // the user's unsaved edits. The message itself is not kept — the effect's
  // toast already reported it and nothing renders it from state.
  on(
    SupplierApiActions.updateAllocationsFailure,
    (state): SupplierState => ({
      ...state,
      allocationsUpdateStatus: 'failed',
    }),
  ),

  on(
    SupplierActions.resetAllocations,
    ManageSurveyActions.resetSurveyDetails,
    (): SupplierState => ({
      allocations: [],
      allocationsLoading: false,
      allocationsError: null,
      allocationsUpdateStatus: 'idle',
      blends: [],
      blendsLoading: false,
      blendsError: null,
      flexibilityPriceParams: null,
      flexibilityPriceParamsLoading: false,
      flexibilityPriceParamsError: null,
    }),
  ),
);
